import { Component, h, Listen, Prop } from '@stencil/core';
import Cookies from 'js-cookie';
import { getProvinceSelectionModalData } from '../../utils/province-list';
import { getFilteredProvincesViaAllowedProvinces } from '../../utils/province-list';
import { getModalWordings } from '../../utils/wordings';
import { getLanguage } from '../../utils/utils';

@Component({
  tag: 'regional-selector-modal',
  shadow: false,
  assetsDirs: ['assets']
})
export class RegionalSelectorModal {

  // Props  
  /**
   * The 'allowed-provinces' attribute is used to show what provinces the consumer will be displayed under the province dropdown. 
   * This is comma separated value where the values are the 2 character keys of provinces e.g. AB,BC,MB,NL,NB,NT,NS,NU,ON,PE,QC,SK,YT,OTHERS.
   */
  @Prop() allowedProvinces: string = '';
  /**
   * The 'error-class' attribute is used to style the error message.
   */
  @Prop({ mutable: true }) errorClass: string = '';
  /**
   * This 'is-open' signals the modal to be displayed or not.
   */
  @Prop() isOpen: boolean = false;
  /**
   * This 'is-popup-message-open' attribute displays the popup-message modal as long as the 'redirect-on-selected-provinces-with-msg' attribute is not empty.
   */
  @Prop({ mutable: true }) isPopupMessageOpen: boolean = false;
  /**
   * The 'ketch-script' attribute is the actual Ketch script of the domain. This varies depending on what application it is, e.g. Websites, Customer Portals, or Quote Engines.
   */
  @Prop() ketchScript!: string;
  /**
   * The 'open-ketch' attribute can be toggled if consumer needs to display the Ketch banner. This is ideal when the consumer doesn't use redirects. 
   */
  @Prop() openKetch: boolean = false;
  /**
   * The 'redirect-on-selected-provinces' attribute is a list of provinces that when the user selects any from the list, they will be redirect.
   * This setting is in conjuction with the 'redirect-on-selected-province-to' as it needs to have a destination.
   */
  @Prop() redirectOnSelectedProvinces: string = '';
  /**
   * The 'redirect-on-selected-provinces-to' attribute is the redirect destination whenever the user selects a certain province.
   */
  @Prop() redirectOnSelectedProvincesTo: string = '';
  /**
   * The 'redirect-on-selected-provinces-with-msg' attribute is the message that the popup message modal will be displaying.
   */
  @Prop() redirectOnSelectedProvincesWithMsg: string = '';
  /**
   * The 'redirect-on-submit-to' attribute is the redirect destination when the user hits the confirm button inside the regional selector modal.
   */
  @Prop() redirectOnSubmitTo: string = '';
  /**
   * The 'redirect-on-submit' attribute is a flag that let's the regional selector mode know that it will redirect the user to a certain destination.
   */
  @Prop() redirectOnSubmit: boolean = false;
  /**
   * The 'redirect-on-selected-province' is a flag that will let the modal know if it will redirect the user to a certain destination.
   */
  @Prop() redirectOnSelectedProvince: boolean = false;
  private hasError: boolean = false;
  private isCookieSet: boolean = false;
  private modal: HTMLDivElement;
  private provinceList = {}
  private selectedProvince: string;
  private showErrorMessage: boolean = false;
  private wordings = {}

  // Lifecycles
  connectedCallback() {
    if (this.ketchScript === '') {
      this.throwException('Missing "ketchScript" property. This property is required.');
    }

    if (this.redirectOnSubmitTo) { this.redirectOnSubmit = true; }
    if (this.redirectOnSelectedProvinces && this.redirectOnSelectedProvincesTo) { this.redirectOnSelectedProvince = true; }
    else if (this.redirectOnSubmit && !this.redirectOnSelectedProvince &&
      ((this.redirectOnSelectedProvinces && !this.redirectOnSelectedProvincesTo) ||
        (!this.redirectOnSelectedProvinces && this.redirectOnSelectedProvincesTo))) {
      this.throwException('When using REDIRECT | SELECTED PROVINCE feature. Please provide values to both "redirect-on-selected-provinces-to" and "redirect-on-selected-provinces" attributes.');
    }
  }

  componentWillLoad() {
    if (this.hasError) { return; }

    // Use a regex to check if the URL contains 'fr-CA or 'fr'
    const language = getLanguage();
    console.log('Selected language: ', language);
    // Set the languge attribute in the HTML document to the selected language
    document.documentElement.lang = language;

    // Check for existing province cookie
    const cookie = Cookies.get("userProvince");
    if (cookie !== "" && cookie !== undefined) {
      this.isCookieSet = true;
    }

    // Load province list and wordings based on the selected language    
    if (this.allowedProvinces !== '') {
      this.provinceList = getFilteredProvincesViaAllowedProvinces(language, this.allowedProvinces);
    } else {
      this.provinceList = getProvinceSelectionModalData(language);
    }

    this.wordings = getModalWordings(language);
  }

  // Functions
  private throwException(message) {
    this.hasError = true;
    console.error(message);
    return;
  }

  private triggerKetch(openKetch = false) {
    if (this.ketchScript !== '') {
      console.log('Send selected province ' + this.selectedProvince + ' from the dropdown.');
      (window as any).sharedProvinceCookie.ketchProvinceCookie(this.selectedProvince, this.ketchScript, openKetch);
    }
  }

  private redirectTo(url) {
    window.location.href = url;
  }

  private getErrorStyle(): string {
    return "mt-2 sm:ml-4 sm:mr-4 text-xs font-semibold text-red-600 rsm-error-message";
  }

  // Events
  @Listen('popupButtonAction')
  popupButtonActionHandler(event: CustomEvent) {
    console.log('Popup message button clicked: ', event.detail);
    this.isPopupMessageOpen = false;
    this.modal.hidden = true;
    if (this.redirectOnSelectedProvincesTo) {
      this.redirectTo(this.redirectOnSelectedProvincesTo);
    }
  }

  async handleProvinceSelect(event: any) {
    console.log(event.target.value);
    this.selectedProvince = event.target.value;
  }

  async handleSubmit() {
    console.log('Confirm button is clicked. OnSubmit is now in progress.');

    this.errorClass = ' hidden';
    // VALIDATE IF THE USER SELECTS 0 FROM THE DROPDOWN 
    if (this.selectedProvince === undefined || this.selectedProvince === '00') {
      const dropdownElem = document.getElementById('dropdown-province') as HTMLElement;
      dropdownElem.focus();
      this.showErrorMessage = true;
      this.errorClass = this.getErrorStyle();
      return;
    }

    // NO REDIRECT FEATURE
    if (!this.redirectOnSubmit && !this.redirectOnSelectedProvince) {
      // FORCE KETCH TO OPEN EVEN WHEN THE ATTRIBUTE WAS NOT SET
      this.triggerKetch(true);
      this.modal.hidden = true;
      return;
    }

    // MAKE SURE TO SET THE OPEN-KETCH to TRUE IF IT NEEDS TO SHOW THE KETCH
    this.triggerKetch(this.openKetch);

    // REDIRECT FEATURE
    if (this.redirectOnSubmit && !this.redirectOnSelectedProvince) { this.redirectTo(this.redirectOnSubmitTo); }
    else if (this.redirectOnSelectedProvince && this.redirectOnSelectedProvinces.split(',').includes(this.selectedProvince)) {
      this.isPopupMessageOpen = (this.redirectOnSelectedProvincesWithMsg ? true : false);
      if (!this.isPopupMessageOpen) {
        this.redirectTo(this.redirectOnSelectedProvincesTo);
      }
    }
    else if (this.redirectOnSubmitTo) {
      this.redirectTo(this.redirectOnSubmitTo);
    }
  }

  render() {
    if (this.isCookieSet || this.hasError) { return; }
    else {
      if (!this.showErrorMessage) { this.errorClass = ' hidden'; }
      return (
        <div id="selector-modal"
          class="regional-selector-modal"
          ref={(el) => this.modal = el as HTMLDivElement}>

          {/* Wrapper */}
          <div class="regional-selector-modal-wrapper">

            {/* Container */}
            <div class="regional-selector-modal-container">

              {/* Header */}
              <header class="regional-selector-modal-header">
                <h2 class="rsm-modal-header-title">{this.wordings['header']}</h2>
              </header>

              {/* Body */}
              <div class="modal-body">
                <div class="pb-2">
                  <label class="rsm-modal-province-label">{this.wordings['provinceDropdownLabel']}</label>          
                    <select id="dropdown-province"
                      class="rsm-province-list-select"
                      onInput={(event) => this.handleProvinceSelect(event)}>
                      <option data-key="00" value="00">{this.wordings['defaultSelectedProvince']}</option>
                      {this.provinceList["provinces"].map((option) => (
                        <option
                          data-key={option.value}
                          value={option.value}
                        >{option.label}</option>
                      ))}
                    </select>                 
                  <p class={this.getErrorStyle() + this.errorClass}>{this.wordings['errorMessage']}</p>
                  <p class="rsm-modal-description">
                    {this.wordings['description']}
                  </p>
                </div>
              </div>

              {/* Footer */}    
                <div class="modal-footer">
                  <regional-selector-button onClick={this.handleSubmit.bind(this)} button-label={this.wordings['confirm']} />
                </div>
            </div>
          </div>

          <popup-message-modal is-open={this.isPopupMessageOpen} message={this.redirectOnSelectedProvincesWithMsg}>
          </popup-message-modal>
        </div>

      );
    }
  }
}
